/**
 * La porte commune des écrans d'équipe (`/ops/...`).
 *
 * Deux défauts réglés ici, une fois pour tous les écrans :
 *
 * 1. **Se connecter sans quitter l'écran.** La cuisine, le bar, la régie, le
 *    bilan… renvoyaient vers `/ops/checkin` pour se connecter, et n'en
 *    revenaient jamais : le barman finissait devant le compteur de la porte.
 *    Chaque écran affiche désormais le même formulaire, sur place, et reste
 *    l'écran qu'on a ouvert une fois connecté.
 * 2. **Dire « votre rôle ne le permet pas ».** Un refus (403) s'affichait
 *    comme une liste vide — « Aucune formule active », « Rien en attente » —
 *    et l'on cherchait une panne qui n'existait pas. `isForbidden` et
 *    `NoAccess` en font une réponse lisible, avec le compte connecté et de
 *    quoi en changer.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { LanguageSwitcher } from '@/design-system/LanguageSwitcher'
import { Card, Container, Overline } from '@/design-system/primitives'

import { ApiError } from './apiClient'
import {
  changeMyPassword,
  clearSession,
  login,
  markPasswordChanged,
  readSession,
  type StaffSession,
} from './staffSession'

/** La règle du serveur (`PasswordChange`) : douze caractères au moins. */
const PASSWORD_MIN = 12

/** Un refus de droit — à distinguer d'une panne ou d'une liste vide. */
export function isForbidden(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403
}

/**
 * Le message d'un refus de l'API, traduit quand on en connaît le code.
 *
 * Les codes métier du soir J (`TILL_WRONG_POST`, `CASH_SESSION_REQUIRED`…)
 * vivent sous `service.error` : un seul dictionnaire pour tous les écrans
 * d'équipe, pour qu'un même refus se lise pareil au bar et à la porte. Un
 * 403 sans code propre se dit « votre rôle ne permet pas ce geste ».
 */
export function useApiErrorText() {
  const { t } = useTranslation()
  return (error: unknown): string | null => {
    if (!error) return null
    if (error instanceof ApiError) {
      return t(`service.error.${error.code}`, {
        defaultValue: error.status === 403 ? t('service.error.FORBIDDEN') : error.message,
      }) as string
    }
    return t('state.errorTitle')
  }
}

/**
 * L'écran, une fois la session ouverte.
 *
 * `children` reçoit la session et de quoi se déconnecter. Se déconnecter
 * ramène au formulaire **de cet écran**, pas à celui de la porte.
 */
export function StaffGate({
  title,
  children,
}: {
  /** Le nom de l'écran, en surtitre du formulaire. */
  title: string
  children: (session: StaffSession, signOut: () => void) => ReactNode
}) {
  const [session, setSession] = useState<StaffSession | null>(() => readSession())

  const signOut = () => {
    clearSession()
    setSession(null)
  }

  if (!session) return <StaffLogin title={title} onSuccess={setSession} />

  // Un compte créé ou réinitialisé par l'administration porte un mot de
  // passe provisoire : le back-office n'ouvre que « Mon compte » tant qu'il
  // n'est pas remplacé. Les écrans d'équipe tiennent la même règle — sinon le
  // mot de passe envoyé par message resterait valable toute la soirée.
  if (session.user.must_change_password) {
    return (
      <PasswordChangeForm
        title={title}
        email={session.user.email}
        onDone={() => setSession(markPasswordChanged())}
        onSignOut={signOut}
      />
    )
  }

  return (
    <>
      {children(session, signOut)}
    </>
  )
}

/**
 * Le nom de l'écran et le choix de la langue, sur la même ligne.
 *
 * Avant la connexion, la langue est souvent la seule chose qu'on puisse
 * régler : un extra germanophone doit pouvoir lire « E-Mail, Passwort »
 * sans demander. Le titre passe à la ligne plutôt que de pousser le
 * sélecteur hors de l'écran.
 */
function TitleRow({ title }: { title?: string | undefined }) {
  return (
    <div className="flex items-center justify-between gap-3">
      {title ? <Overline className="min-w-0 break-words">{title}</Overline> : <span />}
      <LanguageSwitcher />
    </div>
  )
}

/** Le formulaire de connexion, le même sur tous les écrans d'équipe. */
export function StaffLogin({
  title,
  onSuccess,
}: {
  title: string
  onSuccess: (session: StaffSession) => void
}) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    document.title = title
  }, [title])

  return (
    <Container className="flex min-h-dvh max-w-md flex-col justify-center">
      <TitleRow title={title} />
      <h1 className="mt-3 font-display text-display-md text-content-primary">
        {t('staffAccess.loginTitle')}
      </h1>
      <p className="mt-3 text-sm text-content-secondary">{t('staffAccess.loginHint')}</p>

      <form
        className="mt-8 space-y-4"
        onSubmit={(submitEvent) => {
          submitEvent.preventDefault()
          setBusy(true)
          setError(null)
          login(email, password)
            .then(onSuccess)
            .catch((loginError: unknown) => {
              // Un compte verrouillé ou désactivé dit pourquoi ; un mot de
              // passe faux reste un « identifiants incorrects » générique.
              setError(
                loginError instanceof ApiError && loginError.status !== 401
                  ? loginError.message
                  : t('checkin.loginFailed'),
              )
            })
            .finally(() => setBusy(false))
        }}
      >
        <label className="block text-caption text-content-muted">
          {t('checkin.email')}
          <input
            type="email"
            value={email}
            onChange={(changeEvent) => setEmail(changeEvent.target.value)}
            required
            autoComplete="username"
            className="mt-1 h-12 w-full rounded-md border border-line bg-bg-base px-3 text-content-primary"
          />
        </label>
        <label className="block text-caption text-content-muted">
          {t('checkin.password')}
          <input
            type="password"
            value={password}
            onChange={(changeEvent) => setPassword(changeEvent.target.value)}
            required
            autoComplete="current-password"
            className="mt-1 h-12 w-full rounded-md border border-line bg-bg-base px-3 text-content-primary"
          />
        </label>

        {error ? <p className="text-sm text-state-danger">{error}</p> : null}

        <Button type="submit" size="lg" className="w-full" disabled={busy}>
          {t('checkin.login')}
        </Button>
      </form>
    </Container>
  )
}

/**
 * « Votre rôle ne permet pas cet écran » — à la place d'une liste vide.
 *
 * Le compte connecté est rappelé : sur une tablette partagée, la cause la plus
 * fréquente d'un refus est la session de la personne d'avant.
 */
export function NoAccess({
  title,
  onSignOut,
  inline = false,
}: {
  title?: string
  onSignOut?: (() => void) | undefined
  /** Dans un écran qui fonctionne par ailleurs : un encart, pas une page. */
  inline?: boolean
}) {
  const { t } = useTranslation()
  const session = readSession()

  const body = (
    <>
      <p className="font-display text-heading-md text-content-primary">
        {t('staffAccess.noAccessTitle')}
      </p>
      <p className="mt-2 text-sm text-content-secondary">
        {inline ? t('staffAccess.noAccessPart') : t('staffAccess.noAccessBody')}
      </p>
      {session && !inline ? (
        <p className="mt-4 text-caption text-content-muted">
          {t('staffAccess.signedInAs', { email: session.user.email })}
        </p>
      ) : null}
      {onSignOut && !inline ? (
        <Button className="mt-6" variant="outline" onClick={onSignOut}>
          {t('staffAccess.switchAccount')}
        </Button>
      ) : null}
    </>
  )

  if (inline) return <Card className="mt-8">{body}</Card>

  return (
    <Container className="max-w-md py-24">
      <TitleRow title={title} />
      <div className="mt-3">{body}</div>
    </Container>
  )
}

/**
 * Remplacer le mot de passe provisoire, avant d'ouvrir l'écran.
 *
 * Les mêmes règles que « Mon compte » du back-office, vérifiées ici pour
 * prévenir tôt et revérifiées par le serveur : l'ancien mot de passe, un
 * nouveau de douze caractères au moins, différent de l'ancien et de l'e-mail.
 */
function PasswordChangeForm({
  title,
  email,
  onDone,
  onSignOut,
}: {
  title: string
  email: string
  onDone: () => void
  onSignOut: () => void
}) {
  const { t } = useTranslation()
  const errorText = useApiErrorText()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const local =
    next.length > 0 && next.length < PASSWORD_MIN
      ? t('staffAccess.password.tooShort', { min: PASSWORD_MIN })
      : confirm.length > 0 && confirm !== next
        ? t('staffAccess.password.mismatch')
        : next.length > 0 && next === current
          ? t('staffAccess.password.same')
          : next.length > 0 && next.trim().toLowerCase() === email.toLowerCase()
            ? t('staffAccess.password.notEmail')
            : null
  const ready = current.length > 0 && next.length >= PASSWORD_MIN && next === confirm && !local

  const field = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    autoComplete: string,
  ) => (
    <label className="block text-caption text-content-muted">
      {label}
      <input
        type="password"
        value={value}
        onChange={(changeEvent) => onChange(changeEvent.target.value)}
        required
        autoComplete={autoComplete}
        className="mt-1 h-12 w-full rounded-md border border-line bg-bg-base px-3 text-content-primary"
      />
    </label>
  )

  return (
    <Container className="flex min-h-dvh max-w-md flex-col justify-center">
      <TitleRow title={title} />
      <h1 className="mt-3 font-display text-display-md text-content-primary">
        {t('staffAccess.password.title')}
      </h1>
      <p className="mt-3 text-sm text-content-secondary">{t('staffAccess.password.hint')}</p>

      <form
        className="mt-8 space-y-4"
        onSubmit={(submitEvent) => {
          submitEvent.preventDefault()
          if (!ready) return
          setBusy(true)
          setError(null)
          changeMyPassword(current, next)
            .then(onDone)
            .catch((changeError: unknown) => setError(errorText(changeError)))
            .finally(() => setBusy(false))
        }}
      >
        {field(t('staffAccess.password.current'), current, setCurrent, 'current-password')}
        {field(
          t('staffAccess.password.new', { min: PASSWORD_MIN }),
          next,
          setNext,
          'new-password',
        )}
        {field(t('staffAccess.password.confirm'), confirm, setConfirm, 'new-password')}

        {local || error ? (
          <p className="text-sm text-state-danger">{local ?? error}</p>
        ) : null}

        <Button type="submit" size="lg" className="w-full" disabled={busy || !ready}>
          {t('staffAccess.password.submit')}
        </Button>
        <Button variant="ghost" className="w-full" onClick={onSignOut}>
          {t('staffAccess.switchAccount')}
        </Button>
      </form>
    </Container>
  )
}
